import React, { Component } from 'react';
import http from '../modules/api';
import SelectBoxV2 from '../modules/common/SelectBoxV2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  Title,
  Tooltip,
  Legend,
  Filler,
} from 'chart.js';
import { Line, Bar } from 'react-chartjs-2';
import {
  FileText, Eye,
  MousePointer2, ShoppingBag, CircleDollarSign,Percent,
  HelpCircle, TrendingUp, TrendingDown, Minus, Menu
} from 'lucide-react';

ChartJS.register(
  CategoryScale, LinearScale, PointElement, LineElement,
  BarElement, Title, Tooltip, Legend, Filler
);

const DATE_OPTIONS = [
  { text: '최근 7일', value: '7' },
  { text: '최근 30일', value: '30' },
  { text: '최근 60일', value: '60' }
];

class AiRpDashboard extends Component {
  constructor(props) {
    super(props);
    this.state = {
      sync:false,

      isTailwindLoaded: false,

      selectedDays: '7',
      isSidebarOpen: false,

      // API 데이터
      loading: false,
      error: null,

      dashboard: null, // { kpi, trend, funnel, slotTable, range, compare ... }
    };
  }

  componentDidMount() {
      const scriptUrl = "https://cdn.tailwindcss.com";
      const existScript = document.querySelector(`script[src="${scriptUrl}"]`);

      if (existScript) {
        // 이미 로드된 경우
        if (window.tailwind) {
          this.setState({ isTailwindLoaded: true });
        } else {
          // 로드 중이면 load 이벤트 대기
          existScript.addEventListener("load", () => {
            this.setState({ isTailwindLoaded: true });
          });
        }
      } else {
        // 새로 로드
        const script = document.createElement("script");
        script.src = scriptUrl;
        script.async = true;

        script.onload = () => {
          this.setState({ isTailwindLoaded: true });
          console.log("Tailwind 로딩 완료");
        };

        document.head.appendChild(script);
      }

      // 최초 데이터 로드
      this.fetchDashboard();
  }


  componentDidUpdate(prevProps, prevState) {
    // 기간이 바뀌면 다시 조회
    if (prevState.selectedDays !== this.state.selectedDays) {
      this.fetchDashboard();
    }
  }

  toggleSidebar = () => {
    this.setState((prev) => ({ isSidebarOpen: !prev.isSidebarOpen }));
  };

  /**
   * SelectBoxV2가 넘겨주는 형태가 프로젝트마다 다를 수 있는데,
   * 최소한 value 문자열만 잡아내면 됩니다.
   */
  handleDateSelect = (eOrText, eOrValue) => {
    let v = null;

    // 1) e.target.value 형태
    if (eOrValue && eOrValue.target && eOrValue.target.value != null) {
      v = String(eOrValue.target.value);
    } 
    // 2) value 문자열/숫자 직접
    else {
      v = String(eOrValue);
    }

    if (!v) return;
    this.setState({ selectedDays: v });
  };

  /**
   * KST 기준으로 yyyy-mm-dd 만들기 (서버 통계 테이블 stnd_dt 기준)
   */
  toKstYmd = (dateObj) => {
    // dateObj는 JS Date
    // KST로 맞춘 날짜 문자열을 만들기 위해 UTC 기준 보정 (브라우저 로컬이 KST가 아닐 수도 있으니)
    // => "현재 시각 + (KST-UTC) - (localOffset)" 보정
    const tzOffsetMin = dateObj.getTimezoneOffset(); // local offset (분) : KST면 -540
    const kstOffsetMin = -540;
    const diffMin = kstOffsetMin - tzOffsetMin;
    const kstMs = dateObj.getTime() + diffMin * 60 * 1000;
    const d = new Date(kstMs);

    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${y}-${m}-${day}`;
  };

  /**
   * 최근 N일: s = 오늘 포함 N일 전(=오늘 - (N-1)), e = 오늘
   */
  buildRangeByDays = (daysStr) => {
    const days = parseInt(daysStr, 10) || 7;

    const now = new Date();
    const e = this.toKstYmd(now);

    const start = new Date(now.getTime());
    start.setDate(start.getDate() - (days - 1));
    const s = this.toKstYmd(start);

    return { s, e };
  };

  /**
   * 숫자 포맷
   */
  formatNumber = (v) => {
    if (v == null) return '0';
    const n = Number(v);
    if (isNaN(n)) return String(v);
    return n.toLocaleString('ko-KR');
  };

  /**
   * 원 단위 포맷 (API가 숫자 order_amount를 준다고 가정)
   */
  formatWon = (v) => {
    if (v == null) return '₩ 0';
    const n = Number(v);
    if (isNaN(n)) return String(v);
    return `₩ ${n.toLocaleString('ko-KR')}`;
  };

  /**
   * 증감 상태(up/down/same) 만들기
   * - 예: 매출 증감률 rev_chg_pct 를 사용
   */
  buildChgStatus = (pct) => {
    const n = Number(pct);
    if (!isFinite(n) || isNaN(n)) return { status: 'same', rate: 0 };
    if (n > 0) return { status: 'up', rate: n };
    if (n < 0) return { status: 'down', rate: n };
    return { status: 'same', rate: 0 };
  };

  /**
   * 통계 API 호출
   */
  fetchDashboard = () => {
    const days = parseInt(this.state.selectedDays, 10);

    // 날짜 계산: 오늘 포함 days일
    const { s, e } = this.buildRangeByDays(this.state.selectedDays);
    console.log(`데이터 조회: ${s} ~ ${e} (최근 ${days}일)`);

    // (선택) slots 파라미터. 필요 없으면 빼도 됨.
    // 예: this.props.slots = "pageId|htmlId,pageId|htmlId"
    const slots = this.props.slots;

    this.setState({ loading: true, sync: false, error: null });

    http.get("/rest/shop/ai/rp/stats", {
      params: {
        s: s,
        e: e,
        ...(slots ? { slots: slots } : {})
      }
    })
    .then(res => {
      // 서버가 Map으로 내려준 구조 그대로 저장
      this.setState({
        dashboard: res.data,
        sync: true,
        loading: false
      });
    })
    .catch(err => {
      this.setState({ loading: false, sync: false });
      alert("추천 성과 조회 에러");
    });
  };

  /**
   * Trend 차트 데이터: API trend 기반
   */
  getTrendChartData = () => {
    const { dashboard } = this.state;
    const trend = dashboard && dashboard.trend ? dashboard.trend : [];

    const labels = trend.map(r => String(r.stnd_dt || ''));
    const data = trend.map(r => Number(r.rev || 0));

    return {
      labels,
      datasets: [{
        label: '매출 기여액',
        data,
        borderColor: '#00e2e6',
        backgroundColor: 'rgba(0, 226, 230, 0.05)',
        borderWidth: 2,
        fill: true,
        tension: 0.4,
        pointBackgroundColor: '#fff',
        pointRadius: labels.length > 30 ? 0 : 4,
        pointHoverRadius: 6,
        pointHitRadius: 20
      }]
    };
  };

  getFunnelChartData = () => {
    const dashboard = this.state.dashboard;
    const f = dashboard && dashboard.funnel ? dashboard.funnel : {};

    const pv = Number(f.pv || 0);
    const imp = Number(f.imp || 0);
    const click = Number(f.click_cnt || 0);
    const order = Number(f.order_cnt || 0);

    return {
      labels: ['페이지뷰', '실 노출', '클릭', '구매'],
      datasets: [{
        data: [pv, imp, click, order],
        backgroundColor: ['#f3f4f6', '#e0f7fa', '#00e2e6', '#0A1A2F'],
        borderRadius: 6,
        barThickness: 30,
      }]
    };
  };


  render() {
    const { isTailwindLoaded } = this.state;
    if (!isTailwindLoaded) {
      return null;
    }

    const { selectedDays, isSidebarOpen, dashboard, loading, error } = this.state;

    const currentOption = DATE_OPTIONS.find(opt => opt.value === selectedDays);
    const selectedText = currentOption ? currentOption.text : '최근 7일';

    // KPI 가공
    const kpi = dashboard && dashboard.kpi ? dashboard.kpi : null;

    const cur_pv = kpi ? kpi.cur_pv : 0;
    const cur_imp = kpi ? kpi.cur_imp : 0;
    const cur_click = kpi ? kpi.cur_click : 0;

    // 주문/매출은 “구매일 기준 + last-click 7일”로 채운 order_cnt/order_amount
    const cur_order = kpi ? kpi.cur_order : 0;
    const cur_rev = kpi ? kpi.cur_rev : 0;

    // CTR/CVR
    const cur_ctr_pct = kpi ? kpi.cur_ctr_pct : 0;
    const cur_cvr_pct = kpi ? kpi.cur_cvr_pct : 0;

    const revChgPct = kpi ? kpi.rev_chg_pct : 0;
    const revChg = this.buildChgStatus(revChgPct);

    // 도달률(= imp/pv)
    const reachPct = (Number(cur_pv) > 0) ? (Number(cur_imp) / Number(cur_pv) * 100) : 0;

    const orderStatsDaily = dashboard && dashboard.orderStatsDaily ? dashboard.orderStatsDaily : null;

    const total_order_amount = orderStatsDaily ? orderStatsDaily.total_order_amount ? orderStatsDaily.total_order_amount : 0 : 0;

    // Funnel
    const funnel = dashboard && dashboard.funnel ? dashboard.funnel : { pv: 0, imp: 0, click_cnt: 0, order_cnt: 0 };
    const f_pv = Number(funnel.pv || 0);
    const f_imp = Number(funnel.imp || 0);
    const f_click = Number(funnel.click_cnt || 0);
    const f_order = Number(funnel.order_cnt || 0);

    // 퍼널 바 차트는 %로 보여주기 위해 pv를 100으로 정규화
    const base = f_pv > 0 ? f_pv : 1;
    const funnelPct = [
      100,
      Math.round((f_imp / base) * 100),
      Math.round((f_click / base) * 100),
      Math.round((f_order / base) * 100)
    ];

    // Slot Table
    const slotTable = dashboard && dashboard.slotTable ? dashboard.slotTable : [];

    return (
      <div id='common-page-wrapper'>
        <style>{`
          @import url('https://fonts.googleapis.com/css2?family=Lato:wght@400;700;900&family=Noto+Sans+KR:wght@400;500;700;900&display=swap');

          :root {
              --mint-primary: #00e2e6;
              --mint-dark: #00BFC2;
              --mint-light-bg: #E0FAFF;
              --deep-navy: #0A1A2F;
              --gray-text: #6B7280;
              --bg-page: #F9FAFB;
              --border-color: #E5E7EB;
          }

          html:has(#ai-rp-dashboard) {
            overflow: hidden;
          }

          #common-page-wrapper {
              font-family: 'Noto Sans KR', sans-serif;
              background-color: var(--bg-page);
              color: var(--deep-navy);
              -webkit-font-smoothing: antialiased;
          }
          #common-page-wrapper .h-screen { height: calc(100vh - 72px); }            
          @media (max-width: 800px) {
              #common-page-wrapper .h-screen {
                  height: calc(100dvh - 132px);
              }
          }

          .num-font { font-family: 'Lato', sans-serif; }

          .card {
              background: white;
              border: 1px solid var(--border-color);
              border-radius: 12px;
              box-shadow: 0 1px 2px rgba(0,0,0,0.05);
              transition: box-shadow 0.2s ease;
              position: relative;
              display: flex;
              flex-direction: column;
              /* justify-content: space-between; */
              justify-content: flex-start;
          }
          .card:hover { box-shadow: 0 4px 12px rgba(0,0,0,0.08); }

          .card-icon-box {
              width: 44px; height: 44px;
              border-radius: 10px;
              background-color: var(--mint-light-bg);
              color: var(--mint-dark);
              display: flex;
              align-items: center;
              justify-content: center;
              font-size: 20px;
          }

          .card-divider {
              border-top: 1px solid #F3F4F6;
              margin-top: 16px;
              padding-top: 16px;
          }

          .sub-stat {
              font-size: 14px;
              color: var(--gray-text);
              font-weight: 500;
              display: flex;
              align-items: center;
              gap: 6px;
          }
          .sub-stat strong {
              color: var(--mint-dark);
              font-family: 'Lato', sans-serif;
              font-weight: 800;
              font-size: 16px;
          }

          .tooltip-container {
              position: relative;
              display: inline-flex;
              margin-left: 6px;
              cursor: help;
              vertical-align: middle;
          }
          .tooltip-icon { color: #9CA3AF; font-size: 14px; transition: color 0.2s; }
          .tooltip-container:hover .tooltip-icon { color: var(--deep-navy); }
          .tooltip-text {
              visibility: hidden;
              width: 240px;
              background-color: var(--deep-navy);
              color: #fff;
              text-align: left;
              border-radius: 6px;
              padding: 10px 12px;
              position: absolute;
              z-index: 50;
              bottom: 140%;
              left: 50%;
              margin-left: -120px;
              opacity: 0;
              transition: opacity 0.3s;
              font-size: 12px;
              line-height: 1.5;
              font-weight: 400;
              box-shadow: 0 4px 12px rgba(0,0,0,0.15);
              pointer-events: none;
          }
          .tooltip-text::after {
              content: "";
              position: absolute;
              top: 100%;
              left: 50%;
              margin-left: -5px;
              border-width: 5px;
              border-style: solid;
              border-color: var(--deep-navy) transparent transparent transparent;
          }
          .tooltip-container:hover .tooltip-text { visibility: visible; opacity: 1; }

          .custom-table th {
              background-color: #F9FAFB;
              font-weight: 800;
              color: #6B7280;
              font-size: 13px;
              text-transform: uppercase;
              padding: 18px 24px;
              border-bottom: 1px solid var(--border-color);
          }
          .custom-table td {
              padding: 20px 24px;
              border-bottom: 1px solid #F3F4F6;
              color: var(--deep-navy);
              font-size: 14px;
              font-weight: 500;
              vertical-align: middle;
          }
          .custom-table td.number {
              font-family: 'Lato', sans-serif;
              color: #4B5563;
              font-weight: 600;
          }

          .badge { padding: 6px 12px; border-radius: 20px; font-size: 12px; font-weight: 700; display: inline-flex; align-items: center; justify-content: center; gap: 4px; white-space: nowrap; }
          .badge-active { background: #dcfce7; color: #166534; border: 1px solid #bbf7d0; }
          .badge-gray { background: #F3F4F6; color: #4B5563; border: 1px solid #D1D5DB; }
          .badge-mint { background: #e0faff; color: #008c90; border: 1px solid #b2ebf2; }

          .status-up { color: #166534; background: #dcfce7; border: 1px solid #bbf7d0; padding: 2px 8px; border-radius: 6px; font-size: 12px; font-weight: 700; display:inline-flex; align-items:center; gap:2px;}
          .status-down { color: #dc2626; background: #fef2f2; border: 1px solid #fecaca; padding: 2px 8px; border-radius: 6px; font-size: 12px; font-weight: 700; display:inline-flex; align-items:center; gap:2px;}
          .status-neutral { color: #4b5563; background: #f3f4f6; border: 1px solid #e5e7eb; padding: 2px 8px; border-radius: 6px; font-size: 12px; font-weight: 700; display:inline-flex; align-items:center; gap:2px;}

          .no-scrollbar::-webkit-scrollbar { display: none; }
          .no-scrollbar { -ms-overflow-style: none; scrollbar-width: none; }

          @media (min-width: 1536px) {
            #kpi-section {
              overflow-x: auto;
            }
            #kpi-grid {
              grid-template-columns: repeat(4, minmax(210px, 1fr)) minmax(240px, 1fr) minmax(210px, 1fr);
              gap: 12px;
            }
          }
          @media (min-width: 1729px) {
            #kpi-grid {
              gap: 16px;
            }
          }
          @media (min-width: 1900px) {
            #kpi-grid {
              gap: 20px;
            }
          }
        `}</style>

        <div className="flex h-screen overflow-hidden bg-[#f9fafb]">
          <div className="flex-1 flex flex-col min-w-0 overflow-hidden">
            {/* <header className="md:hidden bg-white border-b border-gray-200 px-6 py-4 flex items-center justify-between">
              <div className="flex items-center gap-2">
                <div className="w-8 h-8 bg-[#0A1A2F] rounded-lg flex items-center justify-center text-[#00e2e6] font-black text-sm">R</div>
                <span className="font-bold text-[#0A1A2F]">RoboMD</span>
              </div>
              <button onClick={this.toggleSidebar} className="p-2 -mr-2"><Menu /></button>
            </header> */}

            <main id="ai-rp-dashboard" className="flex-1 overflow-y-auto bg-[#F9FAFB] p-6 md:p-8">
              <div className="max-w-[1800px] mx-auto space-y-8">

                {/* 타이틀 및 Select */}
                <div className="flex flex-col md:flex-row md:items-end justify-between gap-4">
                  <div>
                    <h1 className="text-2xl md:text-3xl font-black text-[#0A1A2F] tracking-tight">추천 성과 분석</h1>
                    <p className="text-sm text-gray-500 mt-1 md:mt-2 font-medium">실시간 AI 추천 성과를 모니터링하세요.</p>
                    {/* {dashboard && dashboard.range && (
                      <p className="text-xs text-gray-400 mt-2 font-medium">
                        조회기간: {dashboard.range.s} ~ {dashboard.range.e}
                      </p>
                    )} */}
                  </div>
                  <div className="w-full md:w-auto">
                    <SelectBoxV2
                      id="dashboard_date_select"
                      list={DATE_OPTIONS}
                      optionTexttName="text"
                      optionValuetName="value"
                      selectedValue={selectedDays}
                      selectedText={selectedText}
                      onClickOption={this.handleDateSelect}
                      width="160px"
                      height="42px"
                      padding="0 16px"
                      pageWrapperName="common-page-wrapper"
                      className="bg-white"
                    />
                  </div>
                </div>

                {/* 로딩/에러 */}
                {/* {loading && (
                  <div className="card p-4 text-sm text-gray-500 font-medium">
                    데이터를 불러오는 중입니다...
                  </div>
                )}
                {error && (
                  <div className="card p-4 text-sm text-red-600 font-bold">
                    조회 실패: {error}
                  </div>
                )} */}

                {/* KPI 카드 */}
                <section id='kpi-section'>
                  <div id ="kpi-grid" className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 2xl:grid-cols-6 gap-4 2xl:gap-6">

                    <div className="card p-6">
                      <div className="flex justify-between items-start mb-2">
                        <div className="flex items-center">
                          <p className="text-sm font-bold text-[#0A1A2F] mt-1">추천 페이지뷰</p>
                          <div className="tooltip-container">
                            <HelpCircle className="tooltip-icon" size={14} />
                            <div className="tooltip-text">
                              추천 위젯이 설치된 페이지(메인, 상세 등)에<br />소비자가 진입한 총 횟수입니다.
                            </div>
                          </div>
                        </div>
                        <div className="card-icon-box"><FileText /></div>
                      </div>
                      <h3 className="text-3xl font-black text-[#0A1A2F] num-font">{this.formatNumber(cur_pv)}</h3>
                      <div className="card-divider">
                        <div className="sub-stat">전체 로드 수</div>
                      </div>
                    </div>

                    <div className="card p-6">
                      <div className="flex justify-between items-start mb-2">
                        <div className="flex items-center">
                          <p className="text-sm font-bold text-[#0A1A2F] mt-1">추천 실 노출수</p>
                          <div className="tooltip-container">
                            <HelpCircle className="tooltip-icon" size={14} />
                            <div className="tooltip-text">
                              소비자가 스크롤하여 실제 추천 영역이<br />화면에 노출된 횟수입니다.
                            </div>
                          </div>
                        </div>
                        <div className="card-icon-box"><Eye /></div>
                      </div>
                      <h3 className="text-3xl font-black text-[#0A1A2F] num-font">{this.formatNumber(cur_imp)}</h3>
                      <div className="card-divider">
                        <div className="sub-stat">도달률 <strong>{reachPct.toFixed(2)}%</strong></div>
                      </div>
                    </div>

                    <div className="card p-6">
                      <div className="flex justify-between items-start mb-2">
                        <p className="text-sm font-bold text-[#0A1A2F] mt-1">추천 클릭수</p>
                        <div className="card-icon-box"><MousePointer2 /></div>
                      </div>
                      <h3 className="text-3xl font-black text-[#0A1A2F] num-font">{this.formatNumber(cur_click)}</h3>
                      <div className="card-divider">
                        <div className="sub-stat">클릭 전환율 <strong>{Number(cur_ctr_pct || 0).toFixed(2)}%</strong></div>
                      </div>
                    </div>

                    <div className="card p-6">
                      <div className="flex justify-between items-start mb-2">
                        <p className="text-sm font-bold text-[#0A1A2F] mt-1">추천 구매 건수</p>
                        <div className="card-icon-box"><ShoppingBag /></div>
                      </div>
                      <h3 className="text-3xl font-black text-[#0A1A2F] num-font">
                        {Number(cur_click) === 0 ? '0' : this.formatNumber(cur_order)}
                        <span className="text-lg text-gray-400 font-bold ml-1">건</span>
                      </h3>
                      <div className="card-divider">
                        <div className="sub-stat">구매 전환율 <strong>{Number(cur_click) === 0 ? '0' : Number(cur_cvr_pct || 0).toFixed(2)}%</strong></div>
                      </div>
                    </div>

                    <div className="card p-6">
                      <div className="flex justify-between items-start mb-2">
                        <p className="text-sm font-bold text-[#0A1A2F] mt-1">추천 기여 매출액</p>
                        <div className="card-icon-box"><CircleDollarSign /></div>
                      </div>
                      <h3 className="text-3xl font-black text-[#0A1A2F] num-font">{Number(cur_click) === 0 ? '0' : this.formatWon(cur_rev)}</h3>
                      <div className="card-divider">
                        <div className="sub-stat">
                          {revChg.status === 'up' && <span className="status-up num-font"><TrendingUp size={12} className="mr-1" /> {revChg.rate.toFixed(2)}%</span>}
                          {revChg.status === 'down' && <span className="status-down num-font"><TrendingDown size={12} className="mr-1" /> {Math.abs(revChg.rate).toFixed(2)}%</span>}
                          {revChg.status === 'same' && <span className="status-neutral num-font"><Minus size={12} className="mr-1" /> 0.00%</span>}
                          <span className="ml-1">직전 기간 대비</span>
                        </div>
                      </div>
                    </div>
                    
                    <div className="card p-6">
                      <div className="flex justify-between items-start mb-2">
                        <p className="text-sm font-bold text-[#0A1A2F] mt-1">추천 기여 매출율</p>
                        <div className="card-icon-box"><Percent /></div>
                      </div>
                      <h3 className="text-3xl font-black text-[#0A1A2F] num-font">
                        {this.formatNumber(
                        Number(total_order_amount) === 0 || Number(cur_click) === 0
                          ? 0
                          : (Number(cur_rev) / Number(total_order_amount) * 100).toFixed(2),
                        2
                      )} <span className="text-lg text-gray-400 font-bold ml-1">%</span>
                      </h3>
                      <div className="card-divider">
                        <div className="sub-stat">전체 매출 대비</div>
                      </div>
                    </div>
                  </div>
                </section>

                {/* 차트 섹션 */}
                <section className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                  <div className="card p-6 lg:col-span-2">
                    <div className="flex justify-between items-center mb-6 pb-4 border-b border-gray-100">
                      <h3 className="text-lg font-bold text-[#0A1A2F]">일별 추천 기여 매출액</h3>
                      <div className="flex items-center gap-4 text-xs font-bold">
                        <div className="flex items-center gap-2">
                          <span className="w-3 h-3 bg-[#00e2e6] rounded-full"></span>
                          <span className="text-[#0A1A2F]">추천 기여</span>
                        </div>
                      </div>
                    </div>
                    <div className="relative h-72 w-full">
                      <Line
                        data={this.getTrendChartData()}
                        options={{
                          responsive: true,
                          maintainAspectRatio: false,
                          plugins: { legend: { display: false } },
                          scales: {
                            x: { grid: { display: false }, ticks: { font: { family: 'Lato', size: 11 }, color: '#6B7280', maxTicksLimit: 8 } },
                            y: { border: { dash: [4, 4] }, beginAtZero: true, grid: { borderDash: [4, 4], color: '#f3f4f6' }, ticks: { font: { family: 'Lato', size: 11 }, color: '#6B7280' } }
                          }
                        }}
                      />
                    </div>
                  </div>

                  <div className="card p-6">
                    <h3 className="text-lg font-bold text-[#0A1A2F] mb-1">고객 여정 퍼널</h3>
                    <p className="text-xs text-gray-400 mb-6 font-medium">페이지뷰 → 실 노출 → 클릭 → 구매</p>
                    <div className="relative h-72 w-full flex items-center justify-center">
                      {/* <Bar
                        data={{
                          labels: ['페이지뷰', '실 노출', '클릭', '구매'],
                          datasets: [{
                            data: funnelPct,
                            backgroundColor: ['#f3f4f6', '#e0f7fa', '#00e2e6', '#0A1A2F'],
                            borderRadius: 6,
                            barThickness: 30
                          }]
                        }}
                        options={{
                          indexAxis: 'y',
                          responsive: true,
                          maintainAspectRatio: false,
                          plugins: { legend: { display: false } },
                          scales: { x: { display: false }, y: { grid: { display: false } } }
                        }}
                      /> */}
                      <Bar 
                        data={this.getFunnelChartData()}
                        options={{
                          indexAxis: 'y',
                          responsive: true,
                          maintainAspectRatio: false,
                          plugins: { legend: { display: false } },
                          scales: { x: { display: false }, y: { grid: { display: false } } },
                          interaction: {
                            mode: 'nearest',
                            axis: 'y',
                            intersect: false     // 막대 위가 아니어도 가까우면 hover
                          },
                        }}
                      />
                    </div>
                  </div>
                </section>

                {/* 테이블 섹션 */}
                <section className="card overflow-hidden !p-0">
                  <div className="p-6 border-b border-gray-100 bg-white">
                    <h3 className="text-lg font-bold text-[#0A1A2F]">진열 영역별 알고리즘 성과</h3>
                    <p className="text-sm text-gray-500 mt-1 font-medium">각 영역(Slot)의 도달률과 전환 성과를 상세 비교합니다.</p>
                  </div>
                  <div className="overflow-x-auto bg-white no-scrollbar">
                    <table className="w-full text-left custom-table min-w-[1340px]">
                      <thead>
                        <tr>
                          <th>진열 위치</th>
                          <th>알고리즘</th>
                          <th className="text-center">상태</th>
                          <th className="text-right">페이지뷰</th>
                          <th className="text-right">실 노출</th>
                          <th className="text-right">도달률</th>
                          <th className="text-right">클릭수</th>
                          <th className="text-right">클릭전환율</th>
                          <th className="text-right">구매건수</th>
                          <th className="text-right">구매전환율</th>
                          <th className="text-right">매출 기여</th>
                        </tr>
                      </thead>
                      <tbody>
                        {slotTable.map((row, idx) => {
                          const pv = Number(row.pv || row.call_cnt || 0);
                          const imp = Number(row.imp || row.impression_cnt || 0);
                          const clickCnt = Number(row.click_cnt || 0);
                          const orderCnt = Number(row.order_cnt || 0);
                          const rev = Number(row.rev || row.order_amount || 0);

                          const reach = row.reach_pct != null ? Number(row.reach_pct) : (pv > 0 ? (imp / pv * 100) : 0);
                          const ctr = row.ctr_pct != null ? Number(row.ctr_pct) : (imp > 0 ? (clickCnt / imp * 100) : 0);
                          const cvr = row.cvr_pct != null ? Number(row.cvr_pct) : (clickCnt > 0 ? (orderCnt / clickCnt * 100) : 0);

                          // 슬롯 라벨 (DB명이면 임시로 page/html 표시)
                          const slotLabel = (row.display_name && row.display_name.trim().length > 0)
                            ? row.display_name
                            : `${row.iframe_page_id} / ${row.iframe_html_id}`;

                          // algo/status는 DB 분리라 했으니 일단 placeholder
                          const algo = row.code_name || '—';
                          const status = (row.install_yn == "Y" && row.display_yn == "Y") ? '운영중' : '미운영중';

                          return (
                            <tr key={idx} className="hover:bg-[#f0fdfe] transition-colors">
                              <td style={{width: 230}}>
                                <div className="font-bold flex items-center gap-2">
                                  <FileText size={16} className="text-gray-400"/>
                                  <div
                                    dangerouslySetInnerHTML={{ __html: row.val3 + "<br/>" + slotLabel }}
                                    style={{ flex: 1, textAlign: "left", maxWidth: 210, whiteSpace: "pre-wrap", lineHeight: 1.2, wordBreak: "keep-all" }}
                                  />
                                </div>
                              </td>
                              <td style={{textAlign:"left", width: 188}}><span className="badge badge-mint" style={{maxWidth: 140, whiteSpace: "pre-wrap", lineHeight: 1.2, wordBreak: "keep-all"}}>{algo}</span></td>
                              <td style={{width: 120}} className="text-center"><span className={"badge "+ (row.install_yn == "Y" && row.display_yn == "Y" ? "badge-active" : "badge-gray")}>{status}</span></td>

                              <td className="text-right number">{this.formatNumber(pv)}</td>
                              <td className="text-right number">{this.formatNumber(imp)}</td>
                              <td className="text-right number">{reach.toFixed(2)}%</td>

                              <td className="text-right number">{this.formatNumber(clickCnt)}</td>
                              <td className="text-right number">{ctr.toFixed(2)}%</td>

                              <td className="text-right number">{this.formatNumber(orderCnt)}</td>
                              <td className="text-right number">{cvr.toFixed(2)}%</td>

                              <td className="text-right number text-[#0A1A2F] font-bold">{this.formatWon(rev)}</td>
                            </tr>
                          );
                        })}
                        {slotTable.length === 0 && !loading && (
                          <tr>
                            <td colSpan={11} className="text-center text-gray-400 py-10">
                              조회된 데이터가 없습니다.
                            </td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                  </div>
                </section>

              </div>
            </main>
          </div>
        </div>
      </div>
    );
  }
}

export default AiRpDashboard;